import { Button } from '@ibe/ui';
import { ArrowLeft } from 'lucide-react';
import { type ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { useUnsavedGuard } from '../../lib/hooks/use-unsaved-guard.js';

/**
 * Every create and edit screen in the portal.
 *
 * Forms are pages rather than modals, and that is a deliberate reversal. A
 * modal is right for a decision — confirm, cancel — and wrong for data entry
 * of any size. It cannot be linked to or reloaded, it traps focus away from
 * the record being edited, it has nowhere to put more than a handful of
 * fields without an inner scrollbar, and on a narrow screen it is a page
 * wearing a border. A tenant has fourteen fields; that was never a dialog.
 *
 * As a page it gets a URL, which is what makes "send me the edit screen for
 * this tenant" possible, and what lets a mis-click be undone with Back.
 *
 * The actions are pinned to the bottom of the form's scroll area rather than
 * sitting at the end of the fields. On a long form the end of the fields is
 * off-screen, and a Save button the user has to hunt for is one they
 * eventually stop trusting is there.
 *
 * `sticky`, not `fixed`. The shell gives the content region its own scrollport,
 * so a fixed bar would be positioned against the viewport instead — spanning
 * under the sidebar and ignoring the region it belongs to. Sticky also keeps
 * the bar in flow, so it reserves its own space rather than needing padding
 * beneath the form to stop it covering the last field.
 */

export interface FormPageProps {
  readonly title: string;
  readonly description?: string;
  /** Where Cancel and the breadcrumb go back to. */
  readonly backTo: string;
  readonly backLabel: string;
  readonly submitLabel: string;
  readonly submitting: boolean;
  readonly onSubmit: () => void;
  readonly children: ReactNode;
  /** Extra actions for the footer — Delete on an edit screen. */
  readonly secondaryActions?: ReactNode;
  /**
   * Whether anything has been typed.
   *
   * Wired to `useUnsavedGuard`, so leaving a half-filled form warns first. Pass
   * `form.formState.isDirty` — the guard is only honest if the value is.
   */
  readonly dirty?: boolean;
}

export function FormPage({
  title,
  description,
  backTo,
  backLabel,
  submitLabel,
  submitting,
  onSubmit,
  children,
  secondaryActions,
  dirty = false,
}: FormPageProps): React.JSX.Element {
  useUnsavedGuard(dirty);
  const navigate = useNavigate();
  const { search } = useLocation();
  const q = backTo.indexOf('?');
  const backToTarget =
    q === -1
      ? { pathname: backTo, search }
      : { pathname: backTo.slice(0, q), search: backTo.slice(q) };

  return (
    <form
      // `noValidate` because validation is Zod's. The browser's own bubble
      // would be a second, differently-worded message competing with ours,
      // and it cannot be styled or announced consistently.
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit();
      }}
    >
      <div className="mx-auto max-w-3xl space-y-section">
        <nav aria-label="Breadcrumb">
          <Link
            to={backToTarget}
            className="inline-flex items-center gap-1 text-caption text-muted-foreground underline-offset-4 hover:underline"
          >
            <ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" />
            {backLabel}
          </Link>
        </nav>

        <header className="space-y-1">
          <h1 className="text-page-title font-semibold">{title}</h1>
          {description === undefined ? null : (
            <p className="text-body text-muted-foreground">{description}</p>
          )}
        </header>

        <div className="rounded-lg border border-border bg-background p-6 shadow-card">
          {children}
        </div>
      </div>

      {/*
       * The negative margins cancel the content region's own padding, so the
       * bar spans its full width and sits flush with the bottom edge. Without
       * `-mb-6` it would stick a padding's height above the bottom and float.
       */}
      <div className="sticky bottom-0 z-overlay -mx-6 -mb-6 mt-section border-t border-border bg-background/95 px-6 backdrop-blur">
        <div className="mx-auto flex max-w-3xl items-center justify-end gap-2 py-3">
          {secondaryActions}
          <Button
            type="button"
            variant="ghost"
            disabled={submitting}
            onClick={() => {
              void navigate(backToTarget);
            }}
          >
            Cancel
          </Button>
          <Button type="submit" disabled={submitting}>
            {submitting ? 'Saving…' : submitLabel}
          </Button>
        </div>
      </div>
    </form>
  );
}
